import { MaxLengthValidatorDirective } from '../../shared/directives/max-length-validator.directive';
import { Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { DecimalPipe } from '@angular/common';
import { MemberService } from '../../core/services/member.service';
import { StudioService } from '../../core/services/studio.service';
import { InvoiceService } from '../../core/services/invoice.service';

const CHARGE_TYPE_NAMES: Record<number, string> = {
  0: 'その他', 1: '月会費', 2: '入会金・事務手数料', 3: 'ロッカー費', 4: 'PT費', 5: '物販',
};

@Component({
    selector: 'app-transaction-edit',
    imports: [MaxLengthValidatorDirective, FormsModule, DecimalPipe],
    template: `
<div class="modal-backdrop fade show" (click)="close()"></div>
<div class="modal fade show d-block" id="editTransactionModal" tabindex="-1" role="dialog" aria-labelledby="editTransactionModalLabel">
  <div class="modal-dialog" role="document">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title" id="editTransactionModalLabel">編集</h5>
        <button type="button" class="close" (click)="close()" aria-label="Close"><span aria-hidden="true">&times;</span></button>
      </div>
      <form novalidate name="myEditForm" #myEditForm="ngForm" (ngSubmit)="save(myEditForm)">
        <div class="modal-body">
          @if (loading) {
            <div class="text-center py-3"><span class="fa fa-spinner fa-spin fa-2x"></span></div>
          } @else {
            <div class="row">
              <div class="col-md-12">
                <!-- 旧版 transaction*.html の modal-body 先頭 <flash-message> 相当 -->
                @if (successMessage) {
                  <div class="alert alert-success alert-dismissible" role="alert">
                    {{ successMessage }}
                    <button type="button" class="close" aria-label="通知を閉じる" (click)="clearSuccessMessage()"><span aria-hidden="true">&times;</span></button>
                  </div>
                } @else { <div style="height: 1px"></div> }
              </div>
              <div class="col-md-12">
                <div class="form-group row"><label class="col-md-4 col-form-label">店舗</label><label class="col-md-8 col-form-label">{{ studio?.name || member?.studio?.name }}</label></div>
                <div class="form-group row"><label class="col-md-4 col-form-label">売上年月</label><label class="col-md-8 col-form-label">{{ form.yearPay }}年{{ form.monthPay }}月</label></div>
                <div class="form-group row"><label class="col-md-4 col-form-label">会員ID</label><label class="col-md-8 col-form-label">{{ member?.memberCode }}</label></div>
                <div class="form-group row"><label class="col-md-4 col-form-label">会員氏名</label><label class="col-md-8 col-form-label" style="word-wrap: break-word;">{{ member?.lastName }}&nbsp;{{ member?.firstName }}</label></div>
                <div class="form-group row"><label class="col-md-4 col-form-label">支払方法</label><label class="col-md-8 col-form-label">{{ paymentMethodName() }}</label></div>
                <div class="form-group row"><label class="col-md-4 col-form-label">売上タイプ</label><div class="col-md-8 col-form-label">{{ chargeTypeName(form.chargeType) }}</div></div>
                <div class="form-group row">
                  <label class="col-md-4 col-form-label">商品</label>
                  <div class="col-md-8" [class.col-form-label]="form.status != 0">
                    @if (form.status == 0) {
                      <select class="form-control" [(ngModel)]="form.product" name="product" (ngModelChange)="updateAmount()" required>
                        <option [ngValue]="null">-- 選択してください --</option>
                        @for (product of filteredProducts; track product.id) {
                          <option [ngValue]="product">(¥{{ productTaxIncluded(product) | number:'1.0-0' }}) {{ product.name }}</option>
                        }
                      </select>
                    } @else { <span>{{ form.product?.name }}</span> }
                  </div>
                </div>
                <div class="form-group row">
                  <label class="col-md-4 col-form-label">調整額</label>
                  <div class="col-md-8">
                    @if (form.status == 0) {
                      <input type="number" class="form-control" [(ngModel)]="form.adjustment" name="adjustment" (ngModelChange)="updateAmount()"
                        [min]="form.product ? -productTaxIncluded(form.product) : 0" max="99999" step="1" placeholder="調整範囲: ±99999">
                    } @else { <span>¥{{ (form.adjustment || 0) | number:'1.0-0' }}</span> }
                  </div>
                </div>
                <div class="form-group row"><label class="col-md-4 col-form-label">金額</label><div class="col-md-8 col-form-label">¥{{ transactionAmount | number:'1.0-0' }}</div></div>
                <div class="form-group row"><label class="col-md-4 col-form-label">明細</label><div class="col-md-8"><input type="text" class="form-control" [(ngModel)]="form.title" name="title" sfMaxLength="200" required></div></div>
                <div class="form-group row"><label class="col-md-4 col-form-label">備考</label><div class="col-md-8"><input type="text" class="form-control" [(ngModel)]="form.note" name="note" sfMaxLength="200"></div></div>
              </div>
            </div>
          }
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="close()"><i class="fa s-icon-cancel"></i>&nbsp;閉じる</button>
          <button type="submit" class="btn btn-success" [disabled]="loading || saving || myEditForm.invalid || isInvalid()"><i class="fa s-icon-save fa-fw"></i>&nbsp;保存</button>
        </div>
      </form>
    </div>
  </div>
</div>
  `
})
export class TransactionEditComponent implements OnInit, OnDestroy {
  private memberService = inject(MemberService);
  private studioService = inject(StudioService);
  private invoiceService = inject(InvoiceService);
  private zone = inject(NgZone);

  @Input({ required: true }) memberId!: number;
  @Input({ required: true }) transactionId!: number;
  @Input() member: any = null;
  @Input() studio: any = null;
  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  form: any = {};
  products: any[] = [];
  originalTransactionProductId: number | null = null;
  transactionAmount = 0;
  loading = true;
  saving = false;
  // 旧版 transaction.js の保存後 Flash.create('success', 'データを更新しました', 5000, true) 相当
  successMessage: string | null = null;
  private successMessageTimer?: ReturnType<typeof setTimeout>;

  get filteredProducts(): any[] {
    return this.products.filter(product => Number(product.productType) === Number(this.form.chargeType));
  }

  async ngOnInit(): Promise<void> {
    const studioId = this.studio?.id ?? this.member?.studio?.id;
    try {
      const [transaction, products] = await Promise.all([
        this.memberService.getOneTransaction(this.memberId, this.transactionId),
        studioId ? this.studioService.getProduct(studioId) : Promise.resolve([]),
      ]);
      this.products = ((products as any[]) ?? []).filter(product => product.status === 0);
      this.form = transaction ?? { id: this.transactionId };
      const productId = this.form.product?.id;
      if (productId) this.form.product = this.products.find(product => product.id === productId) ?? this.form.product;
      this.transactionAmount = Number(this.form.amount || 0);
      this.originalTransactionProductId = this.form.product?.id ?? null;
      this.form.adjustment = this.form.product ? this.transactionAmount - this.productTaxIncluded(this.form.product) : 0;
    } finally {
      this.loading = false;
    }
  }

  ngOnDestroy(): void { this.clearSuccessMessage(); }

  close(): void { this.closed.emit(); }

  clearSuccessMessage(): void {
    if (this.successMessageTimer !== undefined) clearTimeout(this.successMessageTimer);
    this.successMessageTimer = undefined;
    this.successMessage = null;
  }

  private showSuccessMessage(text: string): void {
    this.clearSuccessMessage();
    this.successMessage = text;
    // zone外でタイマーを張り、表示中もアプリを安定状態に保つ。消去時だけzoneへ戻して再描画する
    this.successMessageTimer = this.zone.runOutsideAngular(() =>
      setTimeout(() => this.zone.run(() => this.clearSuccessMessage()), 5000));
  }
  chargeTypeName(id: number): string { return CHARGE_TYPE_NAMES[Number(id)] ?? ''; }
  paymentMethodName(): string {
    const paymentMethod = this.form?.paymentMethod ?? this.member?.payment?.paymentMethod;
    return Number(paymentMethod) === 0 ? '口座振替' : 'クレジット';
  }

  productTaxIncluded(product: any): number {
    if (!product) return 0;
    return Math.floor(Number(product.amount || 0) * (1 + Number(product.taxRate?.rate || 0)));
  }

  updateAmount(): void {
    this.transactionAmount = this.form.product
      ? this.productTaxIncluded(this.form.product) + Number(this.form.adjustment || 0)
      : 0;
  }

  isInvalid(): boolean {
    if (this.loading || !String(this.form.title || '').trim()) return true;
    if (this.form.status != 0) return false;
    if (!this.form.product) return true;
    const adjustment = this.form.adjustment === null || this.form.adjustment === '' ? 0 : Number(this.form.adjustment);
    return !Number.isInteger(adjustment) || adjustment < -this.productTaxIncluded(this.form.product) || adjustment > 99999;
  }

  async save(form: NgForm): Promise<void> {
    if (form.invalid || this.isInvalid()) return;
    this.saving = true;
    try {
      const invoice = this.form.invoice ? { ...this.form.invoice } : null;
      const payload = { ...this.form, id: this.transactionId };
      if (this.form.status == 0 && this.form.product) {
        const rate = Number(this.form.product.taxRate?.rate || 0);
        payload.productId = this.form.product.id;
        if (this.originalTransactionProductId !== payload.productId) payload.amountRate = 1;
        const taxExcluded = Math.ceil(this.transactionAmount / (1 + rate));
        payload.taxAmount = this.transactionAmount - taxExcluded;
        payload.amount = this.transactionAmount;
      } else {
        delete payload.amount;
      }
      for (const key of [
        'yearPay', 'monthPay', 'paymentMethod', 'status', 'member', 'studio', 'product',
        'createdAt', 'updatedAt', 'deletedAt', 'approvedAt', 'cancelledAt', 'capturedAt',
        'invoiceId', 'chargeType', 'isAutoGenerate', 'isNoRetry', 'issuedAt', 'memberId',
        'refundedAt', 'revenueMonth', 'studioId', 'adjustment',
      ]) delete payload[key];
      const saveResult: any = await this.memberService.upsertTransaction(this.memberId, payload);
      // Upsert can return a Sequelize tuple rather than a full transaction.
      // Reload the canonical detail before replacing the visible edit model.
      const savedTransaction = saveResult
        ? await this.memberService.getOneTransaction(this.memberId, this.transactionId)
        : null;
      if (savedTransaction && !Array.isArray(savedTransaction)) {
        this.form = savedTransaction;
        const savedProductId = this.form.product?.id;
        if (savedProductId) {
          this.form.product = this.products.find(product => product.id === savedProductId) ?? this.form.product;
        }
        this.transactionAmount = Number(this.form.amount || 0);
        this.originalTransactionProductId = this.form.product?.id ?? null;
        this.form.adjustment = this.form.product
          ? this.transactionAmount - this.productTaxIncluded(this.form.product)
          : 0;
      }
      if (invoice && (invoice.status === 0 || invoice.status === 1)) {
        await this.invoiceService.updateAmount({ id: invoice.id });
      }
      this.showSuccessMessage('データを更新しました');
      this.saved.emit();
    } finally {
      this.saving = false;
    }
  }
}
